// src/components/tenant/SetupDirectDebit.jsx
import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  console.error(
    "Missing REACT_APP_STRIPE_PUBLISHABLE_KEY – cannot initialise Stripe for Direct Debit."
  );
}

const stripePromise = STRIPE_PUBLISHABLE_KEY
  ? loadStripe(STRIPE_PUBLISHABLE_KEY)
  : null;

function authHeaders() {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function DirectDebitForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    if (!ready) {
      setError("The mandate form is still loading. Please wait a moment.");
      return;
    }

    setLoading(true);
    setError("");

    const { error: stripeError } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/autopay-complete`,
      },
    });

    if (stripeError) {
      console.error("Stripe confirmSetup error:", stripeError);
      setError(
        stripeError.message || "Direct Debit setup failed. Please try again."
      );
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
      <PaymentElement
        onReady={() => {
          setReady(true);
          setError("");
        }}
      />

      <button
        type="submit"
        disabled={!stripe || !elements || loading || !ready}
        className="px-4 py-2 rounded bg-blue-600 text-white disabled:bg-gray-400"
      >
        {loading ? "Setting up…" : "Set up Direct Debit"}
      </button>

      {!ready && <p className="text-sm text-gray-500">Loading mandate form…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </form>
  );
}

export default function SetupDirectDebit({ tenancyId }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    async function createSetupIntent() {
      try {
        setError("");

        const res = await fetch(`${API_BASE}/api/rent/bacs/mandate`, {
          method: "POST",
          headers: authHeaders(),
          credentials: "include",
          body: JSON.stringify({
            tenancyId,
          }),
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(
            `Failed to create Direct Debit setup (${res.status}): ${text}`
          );
        }

        const data = await res.json();

        if (!data.setupIntentClientSecret) {
          throw new Error("No client secret returned from server.");
        }

        setClientSecret(data.setupIntentClientSecret);
      } catch (err) {
        console.error("Error creating Bacs SetupIntent", err);
        setError(err.message || "Unable to start Direct Debit setup.");
      }
    }

    if (tenancyId) {
      createSetupIntent();
    } else {
      setError("Missing tenancy for Direct Debit setup.");
    }
  }, [tenancyId]);

  if (!STRIPE_PUBLISHABLE_KEY || !stripePromise) {
    return (
      <div className="text-sm text-red-600">
        Stripe is not configured correctly for Direct Debit. Please contact the
        property manager.
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-red-600">{error}</div>;
  }

  if (!clientSecret) {
    return <div>Loading Direct Debit setup…</div>;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret }}
      key={clientSecret}
    >
      <DirectDebitForm />
    </Elements>
  );
}
